import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { ChatSurveyMutationResult, ChatSurveyMutationResultDTO } from "./chatSurveyMutationResult";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class StartChatSurveyRPC implements
  LeoRPC<StartChatSurveyRPC.Request, StartChatSurveyRPC.Response, StartChatSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: StartChatSurveyRPC.Request
  ): Promise<LeoRPCResult<StartChatSurveyRPC.Response, StartChatSurveyRPC.Errors.Errors>>;
}

export namespace StartChatSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyLinkId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isStartChatSurveyRPCRequest: boolean = true;
  
    readonly surveyLinkId: LeoUUID;
  
  
    constructor(
    surveyLinkId: LeoUUID
    ) {
      this.surveyLinkId = surveyLinkId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      return new Request(
        surveyLinkId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyLinkId: this.surveyLinkId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `StartChatSurveyRPC(surveyLinkId='${this.surveyLinkId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    result: ChatSurveyMutationResultDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isStartChatSurveyResponse: boolean = true;
  
    readonly result: ChatSurveyMutationResult;
  
  
    constructor(
    result: ChatSurveyMutationResult
    ) {
      this.result = result;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const result = ChatSurveyMutationResult.fromDTO(getObject(dto, "result", InvalidResponseResultError));
      return new Response(
        result
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          result: this.result.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `StartChatSurvey(result='${this.result}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED",
    ChatInterfaceNotEnabled = "CHAT_INTERFACE_NOT_ENABLED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `StartChatSurveyRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `StartChatSurveyRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
      thankYouPageText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
      readonly thankYouPageText: SurveyLocalizedText;
    
      constructor(
      thankYouPageText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
        this.thankYouPageText = thankYouPageText;
      }
    
      static fromDTO(dto: SurveyAlreadySubmittedDTO): SurveyAlreadySubmitted {
        const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseSurveyAlreadySubmittedThankYouPageTextError));
        return new SurveyAlreadySubmitted(
          thankYouPageText
        );
      }
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
          thankYouPageText: this.thankYouPageText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `StartChatSurveyRPC.SURVEY_ALREADY_SUBMITTED(thankYouPageText='${this.thankYouPageText}')`;
    };
    
    
    export interface ChatInterfaceNotEnabledDTO {
      code: string;
    }
    
    export class ChatInterfaceNotEnabled extends LeoRPCError<RPCError> {
      readonly isChatInterfaceNotEnabled: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ChatInterfaceNotEnabled;
        super(code);
      }
    
    
      toDTO(): ChatInterfaceNotEnabledDTO {
        const dto: ChatInterfaceNotEnabledDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ChatInterfaceNotEnabled.prototype.toString = function toString(): string {
      return `StartChatSurveyRPC.CHAT_INTERFACE_NOT_ENABLED()`;
    };
    

    export type Errors = InvalidSurveyLinkId | SurveyIsClosed | SurveyAlreadySubmitted | ChatInterfaceNotEnabled;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | SurveyIsClosedDTO | SurveyAlreadySubmittedDTO | ChatInterfaceNotEnabledDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        case RPCError.SurveyAlreadySubmitted:
          return SurveyAlreadySubmitted.fromDTO(dto as SurveyAlreadySubmittedDTO);
        case RPCError.ChatInterfaceNotEnabled:
          return new ChatInterfaceNotEnabled();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyLinkIdError extends StartChatSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidResponseResultError extends StartChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseResultError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends StartChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends StartChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
export class InvalidResponseSurveyAlreadySubmittedError extends StartChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedThankYouPageTextError extends InvalidResponseSurveyAlreadySubmittedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedThankYouPageTextError";
  }
}
export class InvalidResponseChatInterfaceNotEnabledError extends StartChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseChatInterfaceNotEnabledError";
  }
}

